/**
 * RPC 工具函数 - 用于交易确认轮询
 */

// Ankr API key（与 privy-provider.tsx 保持一致）
const ANKR_API_KEY = 'c6b738a490992d7e86036906e1bcc5eb313cea37a518118a3d7486242d7c1eb0';

// EVM 链 RPC 配置（使用 Ankr 节点）
export const CHAIN_RPC_URLS: Record<number, string> = {
  1: `https://rpc.ankr.com/eth/${ANKR_API_KEY}`,
  56: `https://rpc.ankr.com/bsc/${ANKR_API_KEY}`,
  137: `https://rpc.ankr.com/polygon/${ANKR_API_KEY}`,
  196: `https://rpc.ankr.com/xlayer/${ANKR_API_KEY}`,
  8453: `https://rpc.ankr.com/base/${ANKR_API_KEY}`,
  42161: `https://rpc.ankr.com/arbitrum/${ANKR_API_KEY}`,
  10: `https://rpc.ankr.com/optimism/${ANKR_API_KEY}`,
};

// Solana RPC 配置
export const SOLANA_RPC_URL = process.env.NEXT_WALLET_API_URL || 'https://api.mainnet-beta.solana.com';

// 交易回执类型
interface TransactionReceipt {
  status: string;
  blockNumber: string;
  transactionHash: string;
}

/**
 * 获取交易回执
 * @param rpcUrl RPC 节点地址
 * @param txHash 交易哈希
 * @returns 交易回执或 null
 */
export const getTxReceipt = async (rpcUrl: string, txHash: string): Promise<TransactionReceipt | null> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_getTransactionReceipt',
      params: [txHash],
      id: 1,
    }),
  });
  const data = await response.json();
  return data.result;
};

/**
 * 轮询交易确认状态
 * @param rpcUrl RPC 节点地址
 * @param txHash 交易哈希
 * @param options 轮询选项
 * @returns 交易确认结果
 */
export const pollTxReceipt = async (
  rpcUrl: string,
  txHash: string,
  options?: { interval?: number; maxAttempts?: number }
): Promise<{ status: 'success' | 'failed' }> => {
  const { interval = 2000, maxAttempts = 30 } = options || {};

  for (let i = 0; i < maxAttempts; i++) {
    try {
      const receipt = await getTxReceipt(rpcUrl, txHash);
      if (receipt) {
        return { status: receipt.status === '0x1' ? 'success' : 'failed' };
      }
    } catch (error) {
      console.warn(`轮询交易 ${txHash} 第 ${i + 1} 次失败:`, error);
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  throw new Error('Transaction confirmation timeout');
};

// ============ EVM 直连 RPC 工具函数 ============

/**
 * 获取地址的最新 nonce（用于签名交易）
 * @param rpcUrl RPC 节点地址
 * @param address 钱包地址
 * @returns nonce 值
 */
export const getLatestNonce = async (rpcUrl: string, address: string): Promise<number> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_getTransactionCount',
      params: [address, 'latest'],
      id: 1,
    }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(`获取 nonce 失败: ${data.error.message}`);
  }
  return parseInt(data.result, 16);
};

/**
 * 估算交易 gas
 * @param rpcUrl RPC 节点地址
 * @param txParams 交易参数
 * @returns 估算的 gas 值
 */
export const estimateGas = async (
  rpcUrl: string,
  txParams: { from: string; to: string; value?: string; data?: string }
): Promise<bigint> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_estimateGas',
      params: [{
        from: txParams.from,
        to: txParams.to,
        value: txParams.value || '0x0',
        data: txParams.data || '0x',
      }],
      id: 1,
    }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(`估算 gas 失败: ${data.error.message}`);
  }
  // 返回估算值 * 1.5 作为安全边际
  return BigInt(data.result) * BigInt(15) / BigInt(10);
};

/**
 * 获取当前 gas 价格
 * @param rpcUrl RPC 节点地址
 * @returns gas 价格（wei）
 */
export const getGasPrice = async (rpcUrl: string): Promise<bigint> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_gasPrice',
      params: [],
      id: 1,
    }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(`获取 gasPrice 失败: ${data.error.message}`);
  }
  return BigInt(data.result);
};

/**
 * 直连 RPC 广播已签名的交易
 * @param rpcUrl RPC 节点地址
 * @param signedTx 已签名的交易（hex 格式）
 * @returns 交易哈希
 */
export const sendRawTransaction = async (rpcUrl: string, signedTx: string): Promise<string> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'eth_sendRawTransaction',
      params: [signedTx],
      id: 1,
    }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(`广播交易失败: ${data.error.message}`);
  }
  return data.result;
};

// ============ Solana 直连 RPC 工具函数 ============

/**
 * 直连 Solana RPC 广播已签名的交易
 * @param rpcUrl RPC 节点地址
 * @param signedTx 已签名的交易（base64 格式）
 * @returns 交易签名（base58 格式）
 */
export const sendSolanaTransaction = async (rpcUrl: string, signedTx: string): Promise<string> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'sendTransaction',
      params: [
        signedTx,
        {
          encoding: 'base64',
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        },
      ],
      id: 1,
    }),
  });
  const data = await response.json();
  if (data.error) {
    throw new Error(`Solana 广播交易失败: ${data.error.message}`);
  }
  return data.result;
};

// ============ Solana 交易确认轮询 ============

/**
 * 获取 Solana 交易签名状态
 * @param rpcUrl RPC 节点地址
 * @param signature 交易签名
 * @returns 签名状态或 null
 */
export const getSolanaSignatureStatus = async (
  rpcUrl: string,
  signature: string
): Promise<{ err: any; confirmationStatus: string } | null> => {
  const response = await fetch(rpcUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      jsonrpc: '2.0',
      method: 'getSignatureStatuses',
      params: [[signature], { searchTransactionHistory: true }],
      id: 1,
    }),
  });
  const data = await response.json();
  return data.result?.value?.[0] || null;
};

/**
 * 轮询 Solana 交易确认状态
 * @param rpcUrl RPC 节点地址
 * @param signature 交易签名
 * @param options 轮询选项
 * @returns 交易确认结果
 */
export const pollSolanaTxStatus = async (
  rpcUrl: string,
  signature: string,
  options?: { interval?: number; maxAttempts?: number }
): Promise<{ status: 'success' | 'failed' }> => {
  const { interval = 2000, maxAttempts = 30 } = options || {};

  for (let i = 0; i < maxAttempts; i++) {
    try {
      const status = await getSolanaSignatureStatus(rpcUrl, signature);
      if (status) {
        // err 为 null 表示成功，否则失败
        return { status: status.err === null ? 'success' : 'failed' };
      }
    } catch (error) {
      console.warn(`轮询 Solana 交易 ${signature} 第 ${i + 1} 次失败:`, error);
    }
    await new Promise(resolve => setTimeout(resolve, interval));
  }
  throw new Error('Solana transaction confirmation timeout');
};
